import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { WorldMap } from "@/components/WorldMap";
import { DataFlowBg } from "@/components/DataFlowBg";
import { ArrowRight, Buildings, Coins, Compass, MapPin, Check } from "@phosphor-icons/react";

const APPROACH_ICONS = [Compass, Coins, Buildings];

const Finance = () => {
  const { t } = useLang();
  const approaches = t("finance.approaches");
  const references = t("references.items");

  return (
    <>
      {/* Hero: Markets & References */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <DataFlowBg density={5} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">{t("references.eyebrow")}</div>
          <h1 className="h-display mt-6 text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl">
            {t("references.title")}
          </h1>
        </div>
      </section>

      {/* References list */}
      <section className="relative py-16 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="section-eyebrow">/ 01</div>
            <div className="mt-5 text-white font-heading text-2xl leading-tight">
              {t("finance.mapTitle")}
            </div>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed max-w-sm">
              {t("finance.mapCaption")}
            </p>
            <div className="mt-6 flex items-center gap-6 text-[10px] uppercase tracking-[0.22em] text-slate-400">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#0089C8]" />
                Africa
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#FF6B35]" />
                Deployments
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="references-list">
              {references.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed border-b border-white/[0.08] pb-3">
                  <span className="mt-1 font-mono text-[#FF6B35] text-[11px] shrink-0">/ {String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1">{r}</span>
                  <MapPin size={14} className="mt-1 text-[#0089C8] opacity-60" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="relative py-16 md:py-24 border-t border-white/[0.08] bg-[#131e30]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="section-eyebrow">{t("finance.mapTitle")}</div>
            <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">
              12<span className="text-[#0089C8]">+</span> {t("hero.stat2").replace(/12\+\s*/, "")}
            </h2>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">{t("finance.mapCaption")}</p>
          </div>
          <div className="lg:col-span-7">
            <div className="sharp-card p-6">
              <WorldMap />
            </div>
          </div>
        </div>
      </section>

      {/* Financing */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">{t("finance.eyebrow")}</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white max-w-3xl">
            {t("finance.title")}
          </h2>
          <p className="mt-6 max-w-3xl text-slate-300 text-base sm:text-lg leading-relaxed">
            {t("finance.lead")}
          </p>

          <div className="mt-14">
            <div className="section-eyebrow mb-4">{t("finance.approachTitle")}</div>
            <div className="grid md:grid-cols-3 gap-6">
              {approaches.map((a, i) => {
                const Icon = APPROACH_ICONS[i] || Compass;
                return (
                  <div key={i} className="sharp-card p-8" data-testid={`finance-approach-${i}`}>
                    <div className="flex items-center justify-between">
                      <Icon size={24} weight="duotone" className="text-[#0089C8]" />
                      <span className="font-mono text-[#FF6B35] text-xs">/ {String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="mt-6 text-white text-lg font-heading">{a.t}</div>
                    <p className="mt-3 text-slate-300 text-sm leading-relaxed">{a.d}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-14 border-t border-white/[0.08] pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="text-slate-300 text-sm max-w-xl">
              {t("contact.subtitle")}
            </div>
            <Link
              to="/contact"
              data-testid="finance-contact-cta"
              className="group inline-flex items-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold transition-colors"
            >
              {t("nav.contactCta")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Finance;
